import React, {  useLayoutEffect } from 'react';
import "tabs-pattern/dist/foundation.css";
import {tabs as tabsInit} from "tabs-pattern/src/tabs-ds2"; 
import morehorizontal from "styleguide/icons/ds2/morehorizontal.svg";
import type { TabData } from '../../components/common/config/tabsConfig';
 
// ✅ Declare pljs on Window type
declare global {
  interface Window {
    pljs: {
      getCurrentLang: (el?: HTMLElement) => string;
      createElementFromHTML?: (html: string) => HTMLElement;
      createInnerWrap?: (el: HTMLElement, tag: string, attr: string, value: string) => void;
      getWindowWidth?: () => number;
      getElCount?: (els: NodeListOf<Element>) => number;
    };
  }
}
 
interface TabsProps {
  tabs: TabData[];
  extraProps?: Record<string, any>;
  activeTab?: number;
  setActiveTab?: (id: number) => void;
  dataLength?: Record<number | string, number>;
}
 
const Tabs: React.FC<TabsProps> = ({ tabs: tabsList, extraProps, activeTab, setActiveTab, dataLength }) => {
 
 
  // If activeTab/setActiveTab are not provided, use local state for fallback
  const [internalActiveTab, setInternalActiveTab] = React.useState<number | undefined>(tabsList[0]?.id);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const currentTab = typeof activeTab === 'number' ? activeTab : internalActiveTab;
  const changeTab = setActiveTab || setInternalActiveTab;
  
  useLayoutEffect(() => {
    // Small delay to ensure DOM is fully ready (match reimbursements repo)
    const timer = setTimeout(() => {
      try {
        tabsInit();
        
        // After pattern library initializes, manually sync the active tab with React state
        const tabIndex = tabsList.findIndex(tab => tab.id === currentTab);
        if (tabIndex >= 0) {
          const allTabLinks = document.querySelectorAll('.ds-tabs__link');
          const allPanels = document.querySelectorAll('.ds-tabs__panel');
          
          // Remove active class from all tabs and panels
          allTabLinks.forEach(link => link.classList.remove('-active'));
          allPanels.forEach((panel, index) => {
          const isActive = index === tabIndex;
          panel.classList.toggle('-active', isActive);
          (panel as HTMLElement).style.display = isActive ? 'block' : 'none';
          });
          
          // Add active class to the correct tab and panel
          const targetTab = allTabLinks[tabIndex];
          const targetPanel = allPanels[tabIndex];
          
          if (targetTab) {
            targetTab.classList.add('-active');
            targetTab.setAttribute('aria-selected', 'true');
          }
          if (targetPanel) {
            targetPanel.classList.add('-active');
          }
        }
      } catch (error) {
        console.error('Tabs init error:', error);
      }
      globalThis.dispatchEvent(new Event('resize'));
    }, 10);

    return () => clearTimeout(timer);
  }, [tabsList.length, currentTab , JSON.stringify(dataLength)]);
  
  // Handle overflow menu item clicks using data-tabposition
  React.useEffect(() => {
    const moreList = document.querySelector('.more-button__list');
    if (!moreList) return;

    const handleClick = (e: Event) => {
      const item = (e.target as HTMLElement).closest('.ds-tabs__link-btn') as HTMLElement | null;
      if (!item) return;
      
      const pos = item.dataset.tabposition;
      if (pos) {
        const idx = Number.parseInt(pos) - 1; // convert 1-based to 0-based
        const tab = tabsList[idx];
        if (tab) {
          changeTab(tab.id);
          setMenuOpen(false);
        }
      }
    };

    moreList.addEventListener('click', handleClick);
    return () => moreList.removeEventListener('click', handleClick);
  }, [tabsList, changeTab]);

  // Update overflow menu items with counts
  React.useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout> | undefined;
    let menuTimer: ReturnType<typeof setTimeout> | undefined;

    const updateOverflowCounts = () => {
      if (typeof document === 'undefined') return;

      const moreList = document.querySelector('.more-button__list');
      if (!moreList) return;

      const overflowItems = moreList.querySelectorAll('.ds-tabs__link-btn');
      overflowItems.forEach((item) => {
        const pos = (item as HTMLElement).dataset.tabposition;
        if (pos) {
          const idx = Number.parseInt(pos) - 1;
          const tab = tabsList[idx];
          if (tab) {
            const count = dataLength?.[tab.id];
            const textSpan = item.querySelector('.ds-tabs__text');
            if (textSpan) {
              const countText = typeof count === 'number' ? ` (${count})` : '';
              textSpan.textContent = `${tab.title}${countText}`;
            }
          }
        }
      });
    };

    // Initial update
    updateOverflowCounts();

    // Update when menu opens or window resizes (which may move tabs to/from overflow)
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateOverflowCounts, 100);
    };

    window.addEventListener('resize', handleResize);
    
    // Also update when menu opens
    if (menuOpen) {
      menuTimer = setTimeout(updateOverflowCounts, 50);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (menuTimer) clearTimeout(menuTimer);
    };
  }, [tabsList, dataLength, menuOpen]);
  
  return (
    <div
      className={`ds-tabs -overflow-button grid-container pl-0! pr-0! mx-0!`}
      data-ds-theme="vessel"
      data-ds-variant="basic"
      data-ds-version="2"
      data-pl-variation="overflow-button"
      data-pl-version="2"
      data-lang="en"
      data-panel-announcement-start=""
      data-panel-announcement-end=""
    >
      <div className="ds-tabs__nav-container">
        <div className="ds-tabs__nav ml-0! mr-0!">
          <ul className="ds-tabs__list">
            {tabsList.map((tab) => (
              <li key={tab.id} className="ds-tabs__list-item" style={{ flexShrink: 0.5 }}>
                <button
                  className='ds-tabs__link'
                  onClick={() => {
                    changeTab(tab.id);
                  }}
                >
                  <span className="ds-tabs__text">
                    {tab.title}
                    {(() => {
                      const counts = dataLength;
                      const count = counts ? counts[tab.id as keyof typeof counts] : undefined;
                      return typeof count === 'number' ? ` (${count})` : null;
                    })()}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
            <div className="ds-tabs__nav-more-button">
              <button 
                className="more-button__trigger" 
                aria-expanded={menuOpen} 
                aria-haspopup="true" 
                tabIndex={0}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <span className="screenreader-only">show all</span>
                <img src={morehorizontal} alt="more" width={24} height={24} aria-hidden="true" />
              </button>
              <ul 
                className="more-button__list" 
                style={{ display: menuOpen ? 'block' : 'none' }}
              ></ul>
            </div>
      </div>
      {/* Render all tab components to allow them to fetch data and update counts on mount.
          Hide inactive tabs with display:none while keeping them mounted in the DOM. */}
      <div className="ds-tabs__content">
        {tabsList.map((tab) => {
          const Component = tab.component;
          const isActive = tab.id === currentTab;
          return (
            <div 
              key={tab.id} 
              className={`ds-tabs__panel${isActive ? ' is-active' : ''}`}
              style={{ display: isActive ? 'block' : 'none' }}
            >
              <Component {...(extraProps || {})} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
 
export default Tabs;
 